import Feature from 'ol/Feature';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import Fill from 'ol/style/Fill';
import Point from 'ol/geom/Point';
const DEFAULT_LENS_COLOR = 'rgba(0, 0, 0, 0.6)';
export const DEFAULT_LINE_STROKE = new Stroke({
    color: 'rgba(0, 0, 0, 0.2)',
    width: 1,
});
export const DEFAULT_MINOR_LINE_STROKE = new Stroke({
    color: 'rgba(0, 0, 0, 0.1)',
    width: 0.5,
});
export const DEFAULT_LINE_STYLE = {
    major: DEFAULT_LINE_STROKE,
    minor: DEFAULT_MINOR_LINE_STROKE,
};
export const DEFAULT_CURSOR_COLOR = 'rgba(249, 115, 22, 0.9)';
export const DEFAULT_CURSOR_LABEL_CSS = 'font: 600 10px system-ui, -apple-system, sans-serif; color: #fff; font-variant-numeric: tabular-nums;';
export function createDefaultEdgeLabelText() {
    return new Text({
        font: '600 10px system-ui, -apple-system, sans-serif',
        fill: new Fill({ color: 'rgba(255, 255, 255, 0.9)' }),
        stroke: new Stroke({ color: 'rgba(0, 0, 0, 0.7)', width: 3 }),
    });
}
/** Wrap a `Text` template as an {@link EdgeLabelStyleHandler}. */
export function createDefaultEdgeLabelHandler(template) {
    const baseText = template ?? createDefaultEdgeLabelText();
    return {
        create() {
            const text = baseText.clone();
            const style = new Style({ text });
            const feature = new Feature();
            feature.setGeometry(new Point([0, 0]));
            return { feature, text, style };
        },
        update(slot, ctx) {
            const [minX, minY, maxX, maxY] = ctx.extent;
            const { label } = ctx;
            const coords = label.point.getCoordinates();
            const geom = slot.feature.getGeometry();
            if (!(geom instanceof Point))
                return false;
            slot.text.setText(label.text);
            const cx = coords[0] ?? 0;
            const cy = coords[1] ?? 0;
            const atTop = ctx.xLabelPosition === 'top';
            const atLeft = ctx.yLabelPosition === 'left';
            if (ctx.preplaced) {
                geom.setCoordinates([cx, cy]);
            }
            else if (label.axis === 'x') {
                geom.setCoordinates([cx, atTop ? maxY : minY]);
            }
            else {
                geom.setCoordinates([atLeft ? minX : maxX, cy]);
            }
            // Style by the edge actually anchored to (rotation may move a label off its
            // axis's usual edge); fall back to the axis's configured side.
            const edge = ctx.edge ??
                (label.axis === 'x'
                    ? atTop
                        ? 'top'
                        : 'bottom'
                    : atLeft
                        ? 'left'
                        : 'right');
            styleForEdge(slot.text, edge, ctx);
            return true;
        },
    };
}
/**
 * Align a label's text to sit inside the viewport from `edge`: centered and
 * dropping in from top/bottom, middle-aligned and pushed in from left/right. The
 * inward offset is the config value for that edge's orientation.
 */
function styleForEdge(text, edge, ctx) {
    const horizontal = edge === 'top' || edge === 'bottom';
    const off = horizontal ? ctx.xLabelOffset : ctx.yLabelOffset;
    switch (edge) {
        case 'top':
            text.setTextBaseline('top');
            text.setTextAlign('center');
            text.setOffsetX(0);
            text.setOffsetY(off);
            break;
        case 'bottom':
            text.setTextBaseline('bottom');
            text.setTextAlign('center');
            text.setOffsetX(0);
            text.setOffsetY(-off);
            break;
        case 'left':
            text.setTextBaseline('middle');
            text.setTextAlign('left');
            text.setOffsetX(off);
            text.setOffsetY(0);
            break;
        case 'right':
            text.setTextBaseline('middle');
            text.setTextAlign('right');
            text.setOffsetX(-off);
            text.setOffsetY(0);
            break;
    }
}
/** Library-default cell label handler. */
export function createDefaultCellLabelHandler(options = {}) {
    const fontFamily = options.fontFamily ?? 'system-ui, -apple-system, sans-serif';
    const fontWeight = options.fontWeight ?? 700;
    const fontStyle = options.fontStyle ?? '';
    const fillColor = options.fillColor ??
        ((o) => `rgba(255, 255, 255, ${o.toFixed(2)})`);
    const strokeColor = options.strokeColor ??
        ((o) => `rgba(0, 0, 0, ${(o * 0.8).toFixed(2)})`);
    const strokeWidth = options.strokeWidth ?? 3;
    const [minSize, maxSize] = options.fontSizeRange ?? [12, 32];
    const sizeFactor = options.fontSizeFactor ?? 0.15;
    const [fadeInStart, fullStart, fullEnd, fadeOutEnd] = options.fadeStops ?? [
        40, 80, 400, 800,
    ];
    const peak = options.peakOpacity ?? 0.45;
    const rotation = options.rotation ?? 0;
    const fontPrefix = fontStyle ? `${fontStyle} ${fontWeight}` : `${fontWeight}`;
    const fontCache = new Map();
    const fontOf = (px) => {
        let cached = fontCache.get(px);
        if (cached === undefined) {
            cached = `${fontPrefix} ${px}px ${fontFamily}`;
            fontCache.set(px, cached);
        }
        return cached;
    };
    const fillCache = new Map();
    const fillOf = (opacity) => {
        let cached = fillCache.get(opacity);
        if (cached === undefined) {
            cached = fillColor(opacity);
            fillCache.set(opacity, cached);
        }
        return cached;
    };
    const strokeCache = new Map();
    const strokeOf = (opacity) => {
        let cached = strokeCache.get(opacity);
        if (cached === undefined) {
            cached = strokeColor(opacity);
            strokeCache.set(opacity, cached);
        }
        return cached;
    };
    return {
        create() {
            const fill = new Fill({ color: fillOf(1) });
            const stroke = new Stroke({ color: strokeOf(1), width: strokeWidth });
            const text = new Text({
                font: fontOf(minSize),
                fill,
                stroke,
                textAlign: 'center',
                textBaseline: 'middle',
                rotation,
            });
            const style = new Style({ text });
            return { feature: new Feature(), text, fill, stroke, style };
        },
        update(slot, { label }) {
            const size = label.cellSizePx;
            if (size < fadeInStart || size >= fadeOutEnd)
                return false;
            const rawOpacity = size < fullStart
                ? ((size - fadeInStart) / (fullStart - fadeInStart)) * peak
                : size < fullEnd
                    ? peak
                    : Math.max(0, peak * (1 - (size - fullEnd) / (fadeOutEnd - fullEnd)));
            if (rawOpacity < 0.02)
                return false;
            const opacity = Math.round(rawOpacity * 100) / 100;
            const fontPx = Math.round(Math.min(maxSize, Math.max(minSize, size * sizeFactor)));
            slot.fill.setColor(fillOf(opacity));
            slot.stroke.setColor(strokeOf(opacity));
            slot.text.setFont(fontOf(fontPx));
            slot.text.setText(label.text);
            slot.feature.setGeometry(label.point);
            return true;
        },
    };
}
/** Resolve a {@link GraticuleEdgeLabelStyle} to a handler, or `null` to suppress. */
export function resolveEdgeLabelHandler(input) {
    if (input === undefined)
        return null;
    if (input === true)
        return createDefaultEdgeLabelHandler();
    if (input instanceof Text)
        return createDefaultEdgeLabelHandler(input);
    return input;
}
/** Resolve a {@link GraticuleLineStyle} to an OL `StyleLike` for `setStyle`. */
export function resolveLineStyle(input) {
    const config = input ?? DEFAULT_LINE_STYLE;
    if (config instanceof Stroke) {
        const style = new Style({ stroke: config });
        return style;
    }
    if (isMajorMinor(config)) {
        const major = new Style({ stroke: config.major });
        const minor = new Style({
            stroke: config.minor ?? DEFAULT_MINOR_LINE_STROKE,
        });
        const boundary = config.boundary
            ? new Style({ stroke: config.boundary })
            : major;
        return (feature) => {
            const type = feature.get('gridLineType');
            if (type === 'minor')
                return minor;
            if (type === 'boundary')
                return boundary;
            return major;
        };
    }
    return config;
}
/**
 * Resolve a {@link GraticuleHoverLens} to a fully-filled config, or `null` when
 * disabled. `lineStyle` supplies the default lens colour (the major line ink).
 */
export function resolveHoverLens(input, lineStyle) {
    if (input === undefined || input === false)
        return null;
    return {
        radius: input.radius ?? 120,
        approachFraction: input.approachFraction ?? 0.6,
        approach: input.approach ?? 56,
        clearRadius: input.clearRadius ?? 16,
        boost: input.boost ?? 4,
        color: input.color ?? defaultLensColor(lineStyle),
    };
}
function defaultLensColor(lineStyle) {
    if (lineStyle instanceof Stroke) {
        return colorToCss(lineStyle.getColor()) ?? DEFAULT_LENS_COLOR;
    }
    if (lineStyle !== undefined && isMajorMinor(lineStyle)) {
        return colorToCss(lineStyle.major.getColor()) ?? DEFAULT_LENS_COLOR;
    }
    return DEFAULT_LENS_COLOR;
}
function colorToCss(color) {
    if (typeof color === 'string')
        return color;
    if (Array.isArray(color) && color.length >= 3) {
        const [r, g, b, a] = color;
        return `rgba(${r}, ${g}, ${b}, ${a ?? 1})`;
    }
    return null;
}
export function isMajorMinor(value) {
    return (typeof value === 'object' &&
        value !== null &&
        !Array.isArray(value) &&
        'major' in value &&
        value.major instanceof Stroke);
}
//# sourceMappingURL=style.js.map